import moment from 'moment/min/moment-with-locales'

import { type OpeningHoursProps } from '@/types'
import { trans, CLOSED, getLocale } from '@helpers/translator'
import { useDayTranslations } from '@hooks/useDayTranslations'

moment.locale(getLocale())

// Obtenez l'index du jour actuel en utilisant moment
const todayIndexNumber = moment().isoWeekday() - 1

// Utilisation du hook pour obtenir les clés et traductions des jours
const { daysOfWeekKeys, daysOfWeekTranslations } = useDayTranslations()

const parseTime = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

const formatTime = (minutes: number, separator = ':'): string => {
  const hrs = Math.floor(minutes / 60) % 24
  const mins = minutes % 60
  const formattedHrs = hrs.toString().padStart(2, '0')
  const formattedMins = mins.toString().padStart(2, '0')
  return `${formattedHrs}${separator}${formattedMins}`
}

const isTimeWithinRange = (
  currentTime: number,
  openTime: number | null,
  closeTime: number | null,
): boolean => {
  if (openTime === null) return false
  if (closeTime === null) return currentTime >= openTime

  if (closeTime > openTime) {
    return currentTime >= openTime && currentTime < closeTime
  } else {
    // Fermeture le lendemain matin
    return currentTime >= openTime || currentTime < closeTime
  }
}

export const getOpeningHoursStatus = (
  openingHours: OpeningHoursProps,
): boolean | null => {
  if (typeof openingHours !== 'object') return null

  // Vérifier si au moins un jour a des horaires définis
  const hasAnyOpeningHours = Object.values(openingHours).some(
    (day) =>
      (day.morning && day.morning.open) ||
      (day.afternoon && day.afternoon.open),
  )

  if (!hasAnyOpeningHours) return null // Aucun horaire défini pour aucun jour

  const now = moment()
  const todayKey = daysOfWeekKeys[todayIndexNumber]
  const currentTime = now.hours() * 60 + now.minutes()

  const todayOpenHours = openingHours[todayKey]
  if (
    !todayOpenHours ||
    (!todayOpenHours.morning && !todayOpenHours.afternoon)
  ) {
    return false // Jour de fermeture
  }

  const { morning, afternoon } = todayOpenHours

  // Ouverture continue de matin à après-midi
  if (morning?.open && afternoon?.close && !morning.close && !afternoon.open) {
    const openTime = parseTime(morning.open)
    const closeTime = parseTime(afternoon.close)
    return isTimeWithinRange(currentTime, openTime, closeTime)
  }

  const isMorningOpen: boolean | null = isTimeWithinRange(
    currentTime,
    morning?.open ? parseTime(morning.open) : null,
    morning?.close ? parseTime(morning.close) : null,
  )

  const isAfternoonOpen: boolean | null = isTimeWithinRange(
    currentTime,
    afternoon?.open ? parseTime(afternoon.open) : null,
    afternoon?.close ? parseTime(afternoon.close) : null,
  )

  // Gestion des fermetures après minuit
  if (isMorningOpen && afternoon?.close) {
    const closeTime = parseTime(afternoon.close)
    if (closeTime <= parseTime('04:00')) {
      return currentTime < parseTime('04:00')
    }
  }

  return isMorningOpen || isAfternoonOpen
}

export const getClosingTime = (openingHours: OpeningHoursProps): string => {
  const now = moment()
  const todayKey = daysOfWeekKeys[todayIndexNumber]
  const currentTime = now.hours() * 60 + now.minutes()

  const todayOpenHours = openingHours[todayKey]
  if (
    !todayOpenHours ||
    (!todayOpenHours.morning && !todayOpenHours.afternoon)
  ) {
    return trans(CLOSED) // Jour de fermeture
  }

  const { morning, afternoon } = todayOpenHours

  // Ouverture continue de matin à après-midi
  if (morning?.open && afternoon?.close && !morning.close && !afternoon.open) {
    const openTime = parseTime(morning.open)
    const closeTime = parseTime(afternoon.close)
    if (isTimeWithinRange(currentTime, openTime, closeTime)) {
      return formatTime(closeTime)
    }
  }

  if (
    morning?.open &&
    morning?.close &&
    isTimeWithinRange(
      currentTime,
      parseTime(morning.open),
      parseTime(morning.close),
    )
  ) {
    return morning.close
  }

  if (
    afternoon?.open &&
    afternoon?.close &&
    isTimeWithinRange(
      currentTime,
      parseTime(afternoon.open),
      parseTime(afternoon.close),
    )
  ) {
    return afternoon.close
  }

  if (
    afternoon?.open &&
    !afternoon.close &&
    currentTime >= parseTime(afternoon.open)
  ) {
    return 'Ouvert'
  }

  // Gestion des fermetures après minuit pour les périodes continues
  if (morning?.open && !morning.close && afternoon?.close) {
    const closeTime = parseTime(afternoon.close)
    if (closeTime < parseTime('05:00') && currentTime < closeTime) {
      return formatTime(closeTime)
    }
  }

  return ''
}

export const getOpeningTime = (openingHours: OpeningHoursProps): string => {
  const now = moment()
  const currentTime = now.hours() * 60 + now.minutes()

  for (let i = 0; i < 7; i++) {
    const dayIndex = (todayIndexNumber + i) % 7
    const dayKey = daysOfWeekKeys[dayIndex]
    const todayOpenHours = openingHours[dayKey]

    if (
      !todayOpenHours ||
      (!todayOpenHours.morning && !todayOpenHours.afternoon)
    ) {
      continue // Passer les jours de fermeture
    }

    const { morning, afternoon } = todayOpenHours

    if (i === 0) {
      // Vérifier l'ouverture du matin
      if (morning?.open && currentTime < parseTime(morning.open)) {
        return formatTime(parseTime(morning.open))
      }

      if (afternoon?.open && currentTime < parseTime(afternoon.open)) {
        return formatTime(parseTime(afternoon.open))
      }
    } else {
      // Retourner la première heure d'ouverture du jour suivant avec traduction
      if (morning?.open) {
        return `${formatTime(parseTime(morning.open))} ${daysOfWeekTranslations[dayIndex]}`
      }
      if (afternoon?.open) {
        return `${formatTime(parseTime(afternoon.open))} ${daysOfWeekTranslations[dayIndex]}`
      }
    }
  }

  return trans(CLOSED)
}

export const getOpeningHoursForDay = (
  openingHours: OpeningHoursProps,
  day: string,
) => {
  if (!openingHours[day]) return trans(CLOSED)

  const { morning, afternoon } = openingHours[day]

  if (morning?.open && morning?.close && afternoon?.open && afternoon?.close) {
    return `${formatTime(parseTime(morning.open), 'H')}-${formatTime(parseTime(morning.close), 'H')} ${formatTime(parseTime(afternoon.open), 'H')}-${formatTime(parseTime(afternoon.close), 'H')}`
  } else if (morning?.open && morning?.close) {
    return `${formatTime(parseTime(morning.open), 'H')}-${formatTime(parseTime(morning.close), 'H')}`
  } else if (afternoon?.open && afternoon?.close) {
    return `${formatTime(parseTime(afternoon.open), 'H')}-${formatTime(parseTime(afternoon.close), 'H')}`
  } else if (morning?.open && afternoon?.close) {
    return `${formatTime(parseTime(morning.open), 'H')}-${formatTime(parseTime(afternoon.close), 'H')}`
  }

  return trans(CLOSED)
}

export default getOpeningHoursStatus
